import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams } from 'expo-router';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineMessage from '../../src/components/InlineMessage';
import RefundDialog from '../../src/components/RefundDialog';
import InlineConfirm from '../../src/components/InlineConfirm';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { titleCase, formatPhone } from '../../src/utils/format';

interface TxItem {
  id: string;
  type: string;
  amount: number;
  refundedAmount?: number;
  platformFee: number;
  ownerPayout: number;
  status: string;
  stripePaymentIntentId: string | null;
  paymentBrand: string | null;
  paymentLast4: string | null;
  createdAt: string;
  locationName: string | null;
  bayName: string | null;
  businessName: string | null;
  tierName: string | null;
  durationMinutes: number | null;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  customerAccountNumber: string | null;
  fleetCompanyName: string | null;
  rating: number | null;
  stripeFee?: number | null;
  stripeFeeEstimated?: boolean;
  fleetDiscountPercent?: number | null;
  /** Session rows: funding kind; bundle-funded sessions restore minutes (1.4 P01). */
  paymentMethod?: string | null;
  /** Subscription-wash rows: 'automatic' | 'self_serve' | 'vacuum'. */
  bayType?: string | null;
  creditUsedCents?: number;
  fleetAccountId?: string | null;
  fleetOwnerId?: string | null;
  fleetInvoiced?: boolean;
  /** Latest linked problem report, inlined by the backend. All optional —
   *  older payloads simply omit them, so every read degrades gracefully. */
  issueId?: string | null;
  issueStatus?: string | null;
  issueCategory?: string | null;
  issueDescription?: string | null;
  issueReporterName?: string | null;
  issueReporterEmail?: string | null;
  issueCreatedAt?: string | null;
  issueResolvedAt?: string | null;
  issueRefundedAmountCents?: number | null;
  issueCount?: number;
}

const STATUS_FILTERS = [
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Pending', value: 'pending' },
  { label: 'Failed', value: 'failed' },
  { label: 'Completed', value: 'completed' },
  { label: 'Partially refunded', value: 'partially_refunded' },
  { label: 'Refunded', value: 'refunded' },
];

/** AdminTransactions - paginated transaction list with refund capability and status filtering */
export default function AdminTransactions() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [txs, setTxs] = useState<TxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refunding, setRefunding] = useState<string | null>(null);
  const [confirmRefund, setConfirmRefund] = useState<string | null>(null);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  // Transaction whose reported issue is shown in the detail modal (custom
  // Modal — multi-button Alert.alert collapses to a single OK on web).
  const [issueModalTx, setIssueModalTx] = useState<TxItem | null>(null);

  const { search: searchParam, ownerId: paramOwnerId } = useLocalSearchParams<{ search?: string; ownerId?: string }>();
  const [ownerFilter, setOwnerFilter] = useState<string>(typeof paramOwnerId === 'string' ? paramOwnerId : '');
  const [ownerFilterName, setOwnerFilterName] = useState<string>('');

  const fetchTxs = useCallback(async (p = 1, s = '', status = '', owner = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (status) params.set('status', status);
      if (owner) params.set('ownerId', owner);
      const data = await api<{ transactions: TxItem[]; total: number }>(`/api/admin/transactions?${params}`);
      if (signal?.aborted) return;
      setTxs(data.transactions);
      setTotal(data.total);
      setPage(p);
      if (owner) setOwnerFilterName((data.transactions.find(t => (t as any).businessName) as any)?.businessName ?? '');
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    const initial = typeof searchParam === 'string' ? searchParam : '';
    const owner = typeof paramOwnerId === 'string' ? paramOwnerId : '';
    if (initial) setSearch(initial);
    if (owner) setOwnerFilter(owner);
    fetchTxs(1, initial, '', owner, ac.signal);
    return () => ac.abort();
  }, [searchParam, paramOwnerId]);

  const handleSearch = () => fetchTxs(1, search, statusFilter, ownerFilter);
  const handleFilter = (v: string) => { setStatusFilter(v); fetchTxs(1, search, v, ownerFilter); };
  const handlePage = (p: number) => fetchTxs(p, search, statusFilter, ownerFilter);
  const clearOwnerFilter = () => { setOwnerFilter(''); setOwnerFilterName(''); fetchTxs(1, search, statusFilter, ''); };

  const handleRefund = (tx: TxItem) => {
    setConfirmRefund(tx.id);
    setInlineMsg(null);
  };

  const doRefund = async (tx: TxItem, amount?: number) => {
    setRefunding(tx.id);
    setConfirmRefund(null);
    try {
      if (tx.type === 'fleet-wash') {
        // Fleet washes refund against fleet_transactions (no Stripe); the
        // endpoint is owner-scoped, so pass the owner explicitly as admin.
        await api(`/api/fleet/accounts/${tx.fleetAccountId}/transactions/${tx.id}/refund?ownerId=${tx.fleetOwnerId}`, {
          method: 'POST', body: amount != null ? { amount } : {},
        });
      } else {
        // /api/refunds keys the source table off which id field is sent.
        const body: any = { reason: 'Admin initiated refund', amount };
        if (tx.type === 'bundle-wash') body.bundleWashId = tx.id;
        else if (tx.type === 'subscription-wash') body.subscriptionWashId = tx.id;
        else if (tx.type === 'bundle-purchase') body.purchasedBundleId = tx.id;
        else if (tx.type === 'self-serve' || tx.type === 'vacuum') body.washSessionId = tx.id;
        else body.transactionId = tx.id;
        if (isRestore(tx) || amount == null) delete body.amount;
        await api('/api/refunds', { method: 'POST', body });
      }
      setInlineMsg({ type: 'success', message: isRestore(tx) ? 'Restored to the customer' : 'Refund processed successfully' });
      fetchTxs(page, search, statusFilter);
    } catch (err: any) {
      setInlineMsg({ type: 'error', message: err.message || 'Refund failed' });
    } finally { setRefunding(null); }
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const statusColor = (s: string) => {
    if (s === 'succeeded' || s === 'completed') return '#059669';
    if (s === 'failed') return '#dc2626';
    if (s === 'refunded') return '#7c3aed';
    if (s === 'partially_refunded') return '#d97706';
    return '#d97706';
  };

  const csvEndpoint = `/api/admin/transactions?${new URLSearchParams({ ...(search && { search }), ...(statusFilter && { status: statusFilter }), ...(ownerFilter && { ownerId: ownerFilter }) })}`;

  /** Completed/succeeded/partially-refunded transactions with a payment intent and remaining
   *  refundable balance can still be refunded. Fleet washes refund until invoiced. */
  const isSessionRow = (tx: TxItem) => tx.type === 'self-serve' || tx.type === 'vacuum';
  /** Credit-funded rows restore a wash / minutes / credit instead of moving money (1.4 P01). */
  const isRestore = (tx: TxItem) =>
    tx.type === 'bundle-wash' || tx.type === 'subscription-wash'
    || (isSessionRow(tx) && (tx.paymentMethod === 'bundle' || tx.amount === 0));
  const minutesLabel = (m: number | null | undefined) => `${Math.max(0, Math.round(m ?? 0))} min`;
  const restoreMessage = (tx: TxItem) => {
    if (tx.type === 'bundle-wash') return "Restore 1 wash back to the customer's bundle?";
    if (tx.type === 'subscription-wash') {
      return tx.bayType === 'self_serve' || tx.bayType === 'vacuum'
        ? `Restore ${minutesLabel(tx.durationMinutes)} back to the customer's membership limits?`
        : "Restore 1 wash back to the customer's daily/monthly limits?";
    }
    if (tx.paymentMethod === 'bundle') return `Restore ${minutesLabel(tx.durationMinutes)} back to the customer's bundle?`;
    return 'Nothing was charged to the card. Give the applied wash credit / promo back to the customer?';
  };

  const canRefund = (tx: TxItem) => {
    if (tx.type === 'fleet-wash') {
      return tx.status === 'completed' && !tx.fleetInvoiced && !!tx.fleetAccountId && !!tx.fleetOwnerId;
    }
    if (isRestore(tx)) return tx.status === 'completed';
    return (tx.status === 'completed' || tx.status === 'succeeded' || tx.status === 'partially_refunded')
      && !!tx.stripePaymentIntentId
      && (tx.amount - (tx.refundedAmount ?? 0)) > 0;
  };

  const hasIssue = (tx: TxItem) =>
    tx?.issueId != null || tx?.issueStatus != null || tx?.issueDescription != null;

  const renderItem = ({ item }: { item: TxItem }) => (
    <View>
      <View style={styles.row}>
        <View style={styles.rowBody}>
          <View style={styles.rowHeader}>
            <Text style={styles.amount}>{fmt(item.amount)}</Text>
            <View style={styles.rowHeaderRight}>
              {item.rating != null && (
                <View style={styles.ratingChip}>
                  <Text style={styles.ratingChipText}>★ {item.rating}</Text>
                </View>
              )}
              {hasIssue(item) && (
                <TouchableOpacity
                  onPress={() => setIssueModalTx(item)}
                  hitSlop={8}
                  accessibilityRole="button"
                  accessibilityLabel="View reported issue"
                >
                  <Ionicons name="warning" size={18} color="#d97706" />
                </TouchableOpacity>
              )}
              <View style={[styles.badge, { backgroundColor: statusColor(item.status) + '15' }]}>
                <Text style={[styles.badgeText, { color: statusColor(item.status) }]}>{item.status}</Text>
              </View>
            </View>
          </View>
          {(item.locationName || item.bayName || item.businessName) && (
            <Text style={styles.locationMeta} numberOfLines={2}>
              {item.businessName}{item.locationName ? ` · ${item.locationName}` : ''}{item.bayName ? ` · ${item.bayName}` : ''}{item.tierName ? ` · ${item.tierName}` : ''}
            </Text>
          )}
          {(item.customerName || item.customerEmail || item.fleetCompanyName) && (
            <Text style={styles.locationMeta} numberOfLines={2}>
              {titleCase(item.customerName) || ''}
              {item.customerEmail ? ` · ${item.customerEmail}` : ''}
              {item.customerPhone ? ` · ${formatPhone(item.customerPhone)}` : ''}
              {item.customerAccountNumber ? ` · ${item.customerAccountNumber}` : ''}
              {item.fleetCompanyName ? ` · Fleet: ${item.fleetCompanyName}` : ''}
            </Text>
          )}
          <Text style={styles.meta} numberOfLines={2}>
            {item.type}{item.paymentBrand ? ` · ${item.paymentBrand} ••${item.paymentLast4}` : ''} · Fee {fmt(item.platformFee)}
            {item.stripeFee != null && item.stripeFee > 0 ? ` · Stripe ${fmt(item.stripeFee)}${item.stripeFeeEstimated ? ' (est.)' : ''}` : ''}
            {` · Net ${fmt(item.ownerPayout - (item.stripeFee ?? 0))}`}
            {(item.fleetDiscountPercent ?? 0) > 0 ? ` · ${item.fleetDiscountPercent}% fleet` : ''}
            {(item.creditUsedCents ?? 0) > 0 ? ` · credit ${fmt(item.creditUsedCents!)}` : ''}
            {` · ${new Date(item.createdAt).toLocaleString()}`}
          </Text>
        </View>
        {canRefund(item) && (
          <TouchableOpacity
            style={styles.refundBtn}
            onPress={() => handleRefund(item)}
            disabled={refunding === item.id}
          >
            {refunding === item.id ? (
              <ActivityIndicator size="small" color={colors.danger} />
            ) : (
              <>
                <Ionicons name="return-down-back" size={14} color={colors.danger} />
                <Text style={styles.refundText}>{isRestore(item) ? 'Restore' : 'Refund'}</Text>
              </>
            )}
          </TouchableOpacity>
        )}
      </View>
      {confirmRefund === item.id && (
        isRestore(item) ? (
          <InlineConfirm
            message={restoreMessage(item)}
            visible={true}
            onConfirm={() => doRefund(item)}
            onCancel={() => setConfirmRefund(null)}
            confirmLabel="Restore"
            loading={refunding === item.id}
            variant="warning"
          />
        ) : (
          <RefundDialog
            visible={true}
            originalCents={item.amount}
            alreadyRefundedCents={item.refundedAmount ?? 0}
            subtitle={item.type === 'fleet-wash'
              ? `Refund this fleet wash? The remaining amount is what gets invoiced to ${item.fleetCompanyName || 'the fleet'}.`
              : 'Refund this transaction'}
            loading={refunding === item.id}
            onCancel={() => setConfirmRefund(null)}
            onConfirm={(amount) => doRefund(item, amount)}
          />
        )
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      {ownerFilter !== '' && (
        <TouchableOpacity
          onPress={clearOwnerFilter}
          style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: 6, backgroundColor: colors.primary + '15', borderWidth: 1, borderColor: colors.primary, borderRadius: 16, paddingHorizontal: 12, paddingVertical: 5, marginBottom: 10 }}
        >
          <Ionicons name="business" size={14} color={colors.primary} />
          <Text style={{ color: colors.primary, fontSize: 13, fontWeight: '600' }}>
            {ownerFilterName ? `Owner: ${ownerFilterName}` : 'Filtered to one owner'}
          </Text>
          <Ionicons name="close-circle" size={16} color={colors.primary} />
        </TouchableOpacity>
      )}
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={handleSearch}
        total={total} page={page} limit={20} onPageChange={handlePage}
        csvEndpoint={csvEndpoint} csvFilename="transactions.csv" label="transaction"
        filterOptions={STATUS_FILTERS} activeFilter={statusFilter} onFilterChange={handleFilter}
      />
      <InlineMessage
        type={inlineMsg?.type ?? 'success'}
        message={inlineMsg?.message ?? ''}
        visible={!!inlineMsg}
        onDismiss={() => setInlineMsg(null)}
        autoDismissMs={4000}
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />}
          data={txs}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No transactions found</Text>}
        />
      )}
      {issueModalTx && (() => {
        // Optional-chained reads with fallbacks — the backend inlines the
        // latest linked problem report; absent fields degrade to em-dash.
        const tx = issueModalTx;
        const description = tx.issueDescription ?? null;
        const reporter = tx.issueReporterName ?? tx.issueReporterEmail
          ?? tx.customerName ?? tx.customerEmail ?? null;
        const reportedAt = tx.issueCreatedAt ?? null;
        const status = tx.issueStatus ?? null;
        return (
          <Modal visible transparent animationType="fade" onRequestClose={() => setIssueModalTx(null)}>
            <View style={styles.modalOverlay}>
              <View style={styles.modalCard}>
                <View style={styles.modalHeader}>
                  <Ionicons name="warning" size={20} color="#d97706" />
                  <Text style={styles.modalTitle}>Reported Issue</Text>
                  {status && (
                    <View style={[styles.badge, { backgroundColor: (status === 'resolved' ? '#059669' : '#d97706') + '15' }]}>
                      <Text style={[styles.badgeText, { color: status === 'resolved' ? '#059669' : '#d97706' }]}>{status}</Text>
                    </View>
                  )}
                </View>
                {tx.issueCategory ? <Text style={styles.modalCategory}>{tx.issueCategory}</Text> : null}
                <Text style={styles.modalDescription}>{description || 'No description provided.'}</Text>
                {(tx.issueCount ?? 0) > 1 && (
                  <Text style={styles.modalMetaLabel}>Latest of {tx.issueCount} reports on this transaction.</Text>
                )}
                <View style={styles.modalMetaRow}>
                  <Text style={styles.modalMetaLabel}>Reported by</Text>
                  <Text style={styles.modalMetaValue}>
                    {reporter ? (reporter.includes('@') ? reporter : titleCase(reporter) || reporter) : '—'}
                  </Text>
                </View>
                <View style={styles.modalMetaRow}>
                  <Text style={styles.modalMetaLabel}>Date</Text>
                  <Text style={styles.modalMetaValue}>{reportedAt ? new Date(reportedAt).toLocaleString() : '—'}</Text>
                </View>
                <TouchableOpacity style={styles.modalCloseBtn} onPress={() => setIssueModalTx(null)}>
                  <Text style={styles.modalCloseText}>Close</Text>
                </TouchableOpacity>
              </View>
            </View>
          </Modal>
        );
      })()}
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  amount: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  locationMeta: { fontSize: fontSize.xs, color: c.text, marginTop: 4, fontWeight: fontWeight.medium },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  refundBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 10, paddingVertical: 6,
    borderRadius: borderRadius.sm, backgroundColor: '#dc262610',
    marginLeft: spacing.sm,
  },
  refundText: { fontSize: fontSize.xs, color: c.danger, fontWeight: fontWeight.semibold },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  rowHeaderRight: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  ratingChip: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, backgroundColor: '#d9770615' },
  ratingChipText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: '#d97706' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  modalCard: { width: '100%', maxWidth: 440, backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg },
  modalHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md },
  modalTitle: { flex: 1, fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  modalCategory: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginBottom: spacing.xs, textTransform: 'uppercase', letterSpacing: 0.5 },
  modalDescription: { fontSize: fontSize.sm, color: c.text, lineHeight: 20, marginBottom: spacing.md },
  modalMetaRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs },
  modalMetaLabel: { fontSize: fontSize.xs, color: c.textSecondary },
  modalMetaValue: { fontSize: fontSize.xs, color: c.text, fontWeight: fontWeight.medium, flexShrink: 1, textAlign: 'right' },
  modalCloseBtn: { marginTop: spacing.md, alignSelf: 'flex-end', paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderRadius: borderRadius.md, backgroundColor: c.primary },
  modalCloseText: { color: c.white, fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
